import { FiCheck, FiX, FiClock } from 'react-icons/fi'

// Funil 3 etapas do follow-up de líder (v2) — exibição do estado + registro
// MANUAL por etapa (quando a líder responde por ligação/áudio em vez dos
// botões do WhatsApp). Usado na sidebar (ContactProfilePanel) e no drawer do
// LeaderFollowupPanel. O registro chama POST /api/leader-followups/:id/stage-manual,
// que alimenta a MESMA máquina de estados dos botões do WhatsApp — os botões
// antigos de desfecho único ("Deu certo/Não deu") gravavam o derivado cru e
// deixavam o funil órfão; foram aposentados em 2026-07-10.

export interface StageFields {
  stage: number | null
  contacted: 'yes' | 'pending' | 'unreachable' | null
  explained: 'yes' | 'partial' | 'refused' | null
  interest: 'yes' | 'thinking' | 'no' | null
}

export type StageReply =
  | 'falei' | 'nao_consegui' | 'nao_atende'
  | 'expliquei' | 'por_cima' | 'nao_quis'
  | 'quer_cadastrar' | 'pensar' | 'sem_interesse'

// Rótulos PT dos motivos que a máquina grava em leader_outcome_reason.
export const REASON_LABEL: Record<string, string> = {
  quer_cadastrar: 'quer se cadastrar',
  sem_interesse: 'sem interesse',
  nao_quis_ouvir: 'não quis ouvir a proposta',
  numero_inalcancavel: 'número não atende',
  sem_contato: 'sem contato',
  ficou_pensando: 'ficou de pensar',
}

const BTN_BASE = 'h-7 text-[10px] font-medium rounded-md border disabled:opacity-40 transition-colors'
const BTN_GOOD = `${BTN_BASE} border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20`
const BTN_WARN = `${BTN_BASE} border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20`
const BTN_BAD = `${BTN_BASE} border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-500/20`

// As 3 perguntas — mesmos textos/opções que a líder vê no WhatsApp.
const STAGE_ASK: Record<number, { q: string; opts: { reply: StageReply; label: string; cls: string; title: string }[] }> = {
  1: {
    q: 'Conseguiu falar com o lead?',
    opts: [
      { reply: 'falei', label: 'Falei', cls: BTN_GOOD, title: 'A líder falou com o lead — avança pra pergunta da explicação' },
      { reply: 'nao_consegui', label: 'Ainda não', cls: BTN_WARN, title: 'Ainda não conseguiu contato — re-pergunta automática em 24h (máx. 2×)' },
      { reply: 'nao_atende', label: 'Nº não atende', cls: BTN_BAD, title: 'Número inalcançável — encerra o acompanhamento' },
    ],
  },
  2: {
    q: 'Conseguiu apresentar a proposta?',
    opts: [
      { reply: 'expliquei', label: 'Explicou', cls: BTN_GOOD, title: 'Proposta apresentada — avança pra pergunta do interesse' },
      { reply: 'por_cima', label: 'Só por cima', cls: BTN_WARN, title: 'Explicou parcialmente — re-pergunta automática em 2 dias' },
      { reply: 'nao_quis', label: 'Não quis ouvir', cls: BTN_BAD, title: 'Lead não quis ouvir a proposta — encerra' },
    ],
  },
  3: {
    q: 'O lead tem interesse em se cadastrar?',
    opts: [
      { reply: 'quer_cadastrar', label: 'Quer cadastrar!', cls: BTN_GOOD, title: 'Interesse confirmado — conclui com sucesso (o cadastro é feito pela líder)' },
      { reply: 'pensar', label: 'Vai pensar', cls: BTN_WARN, title: 'Ficou de pensar — encerra (a líder segue o contato por conta dela)' },
      { reply: 'sem_interesse', label: 'Sem interesse', cls: BTN_BAD, title: 'Sem interesse — encerra' },
    ],
  },
}

// Chips do estado do funil: ① Falou ② Explicou ③ Interesse.
export function LeaderStageFunnel({ f, neu = false }: { f: StageFields; neu?: boolean }) {
  const steps: { label: string; state: 'ok' | 'warn' | 'bad' | 'none'; txt: string }[] = [
    {
      label: '1·Falou',
      state: f.contacted === 'yes' ? 'ok' : f.contacted === 'unreachable' ? 'bad' : f.contacted === 'pending' ? 'warn' : 'none',
      txt: f.contacted === 'yes' ? 'sim' : f.contacted === 'pending' ? 'ainda não' : f.contacted === 'unreachable' ? 'não atende' : '—',
    },
    {
      label: '2·Explicou',
      state: f.explained === 'yes' ? 'ok' : f.explained === 'refused' ? 'bad' : f.explained === 'partial' ? 'warn' : 'none',
      txt: f.explained === 'yes' ? 'sim' : f.explained === 'partial' ? 'por cima' : f.explained === 'refused' ? 'não quis' : '—',
    },
    {
      label: '3·Interesse',
      state: f.interest === 'yes' ? 'ok' : f.interest === 'no' ? 'bad' : f.interest === 'thinking' ? 'warn' : 'none',
      txt: f.interest === 'yes' ? 'sim!' : f.interest === 'thinking' ? 'pensando' : f.interest === 'no' ? 'não' : '—',
    },
  ]
  const cls = { ok: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', warn: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', bad: 'bg-rose-500/15 text-rose-600 dark:text-rose-400', none: neu ? 'neu-chip' : 'bg-secondary/70 text-muted-foreground' }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {steps.map(s => (
        <span key={s.label} title={`${s.label.slice(2)}: ${s.txt}`}
          className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium tabular-nums ${cls[s.state]}`}>
          {s.label} {s.txt}
        </span>
      ))}
    </div>
  )
}

// Controle completo: funil + pergunta da etapa atual (registro manual) + atalhos.
// canWrite=false (legado em voo/concluído) → só exibição.
export function LeaderStageControl({ f, status, nudgeCount, leaderOutcome, leaderOutcomeReason, busy, onReply, neu = false }: {
  f: StageFields
  status: string
  nudgeCount: number
  leaderOutcome: 'success' | 'failure' | 'trying' | null
  leaderOutcomeReason: string | null
  busy: boolean
  onReply: (reply: StageReply) => void
  neu?: boolean
}) {
  const concluded = status === 'concluded'
  const isLegacy = f.stage == null && (nudgeCount > 0 || concluded)
  const curStage = f.stage == null ? 1 : Number(f.stage)

  if (concluded) {
    const reasonLabel = leaderOutcomeReason ? (REASON_LABEL[leaderOutcomeReason] || leaderOutcomeReason) : null
    return (
      <div className="space-y-1">
        {!isLegacy && <LeaderStageFunnel f={f} neu={neu} />}
        <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
          {leaderOutcome === 'success' ? <FiCheck className="w-3 h-3 text-emerald-500" /> : leaderOutcome === 'failure' ? <FiX className="w-3 h-3 text-rose-500" /> : <FiClock className="w-3 h-3 text-amber-500" />}
          {leaderOutcome === 'success' ? 'Convertido' : leaderOutcome === 'failure' ? 'Não converteu' : 'Concluído'}
          {reasonLabel ? ` · ${reasonLabel}` : ''}
          {isLegacy && <span className="text-muted-foreground/60" title="Concluído pelo fluxo antigo de desfecho único, antes do funil de 3 etapas">(fluxo antigo)</span>}
        </div>
      </div>
    )
  }

  if (isLegacy) {
    // Fluxo antigo em voo: read-only (forward-only) — morre no fluxo v1.
    return (
      <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1"
        title="Acompanhamento iniciado antes do funil de 3 etapas — segue o fluxo antigo até concluir; sem registro manual por etapa">
        <FiClock className="w-3 h-3" /> fluxo antigo em andamento
      </div>
    )
  }

  const ask = STAGE_ASK[curStage] || STAGE_ASK[1]
  return (
    <div className="space-y-1.5">
      <LeaderStageFunnel f={f} neu={neu} />
      <div className="text-[10px] text-muted-foreground" title="Registro manual: use quando a líder respondeu por ligação/áudio — alimenta o mesmo funil dos botões do WhatsApp">
        {ask.q} <span className="text-muted-foreground/60">(registro manual)</span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {ask.opts.map(o => (
          <button key={o.reply} disabled={busy} title={o.title} onClick={() => onReply(o.reply)}
            className={neu ? `${o.cls} neu-relief` : o.cls}>
            {o.label}
          </button>
        ))}
      </div>
      {curStage < 3 && (
        <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
          <span title="Quando a líder já contou o desfecho completo por telefone">Encerrar direto:</span>
          <button disabled={busy} onClick={() => onReply('quer_cadastrar')}
            title="Conclui com sucesso — as etapas anteriores contam como feitas (o cadastro é feito pela líder)"
            className="text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-40">quer se cadastrar</button>
          <span>·</span>
          <button disabled={busy} onClick={() => onReply('sem_interesse')}
            title="Conclui sem interesse — marca o contato como feito; a etapa da explicação fica como estiver"
            className="text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-40">sem interesse</button>
        </div>
      )}
    </div>
  )
}
